import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { adminSwal as Swal } from "./adminSwal";
import { adminCoordenacoesApi } from "../../../api/adminCoordenacoes";
import { adminApi } from "../../../api/admin";
import { TipoUsuario } from "../../../api/usuarios";
import "./AdminInstituicaoCoordenacoes.scss";

// Constrói o HTML do modal de criação/edição: nome + multiselect de coordenadores.
function montarHtmlSeletorCoordenadores(usuarios, selecionadosIds = []) {
  if (usuarios.length === 0) {
    return `<p style="margin:0.5rem 0;color:#b94a48;">Nenhum coordenador disponível nesta instituição. Crie um usuário do tipo Coordenador antes.</p>`;
  }
  const sel = new Set(selecionadosIds.map(String));
  const opts = usuarios
    .map(
      (u) =>
        `<option value="${u.id}" ${sel.has(String(u.id)) ? "selected" : ""}>${u.nome} (${u.email})</option>`,
    )
    .join("");
  return `
    <label class="swal2-label" style="display:block;text-align:left;margin-top:0.75rem;font-size:0.875rem;">
      Coordenadores vinculados (segure Ctrl/Cmd para múltiplos)
    </label>
    <select id="sw-coords" class="swal2-input" multiple size="6" style="height:auto;">
      ${opts}
    </select>
  `;
}

export default function AdminInstituicaoCoordenacoes() {
  const { id: instId } = useParams();
  const [coords, setCoords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [aberta, setAberta] = useState(null);
  const [detalhes, setDetalhes] = useState({});

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      setCoords(await adminCoordenacoesApi.listar(instId));
    } catch (err) {
      Swal.fire("Erro", err?.message || "Falha ao listar", "error");
    } finally {
      setLoading(false);
    }
  }, [instId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const carregarDetalhes = async (coordId) => {
    try {
      const detalhe = await adminCoordenacoesApi.obter(instId, coordId);
      setDetalhes((prev) => ({ ...prev, [coordId]: detalhe }));
    } catch (err) {
      Swal.fire("Erro", err?.message || "Falha ao carregar detalhes", "error");
    }
  };

  const buscarCoordenadoresDisponiveis = async () => {
    const res = await adminApi.listarUsuarios({
      instituicaoId: instId,
      tipo: TipoUsuario.Coordenador,
      pageSize: 200,
    });
    return res.items || [];
  };

  const onCriar = async () => {
    let usuariosCoord = [];
    try {
      usuariosCoord = await buscarCoordenadoresDisponiveis();
    } catch (err) {
      Swal.fire("Erro", err?.message || "Falha ao carregar coordenadores", "error");
      return;
    }

    const { value, isConfirmed } = await Swal.fire({
      title: "Nova coordenação",
      html: `
        <input id="sw-nome" class="swal2-input" placeholder="Nome da coordenação" />
        ${montarHtmlSeletorCoordenadores(usuariosCoord)}
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Criar",
      cancelButtonText: "Cancelar",
      preConfirm: () => {
        const nome = document.getElementById("sw-nome").value.trim();
        if (!nome) {
          Swal.showValidationMessage("Nome obrigatório");
          return false;
        }
        const select = document.getElementById("sw-coords");
        const coordIds = select
          ? Array.from(select.selectedOptions).map((o) => o.value)
          : [];
        if (coordIds.length === 0) {
          Swal.showValidationMessage(
            "Selecione ao menos um coordenador para a coordenação.",
          );
          return false;
        }
        return { nome, coordenadorIds: coordIds };
      },
    });
    if (!isConfirmed) return;

    try {
      await adminCoordenacoesApi.criar(instId, value);
      Swal.fire("OK", "Coordenação criada", "success");
      carregar();
    } catch (err) {
      Swal.fire("Erro", err?.message || "Falha ao criar", "error");
    }
  };

  const onEditar = async (c) => {
    const { value, isConfirmed } = await Swal.fire({
      title: "Editar coordenação",
      input: "text",
      inputValue: c.nome,
      showCancelButton: true,
      confirmButtonText: "Salvar",
      cancelButtonText: "Cancelar",
      preConfirm: (v) => {
        if (!v?.trim()) {
          Swal.showValidationMessage("Nome obrigatório");
          return false;
        }
        return { nome: v.trim() };
      },
    });
    if (!isConfirmed) return;
    try {
      await adminCoordenacoesApi.editar(instId, c.id, value);
      Swal.fire("OK", "Coordenação atualizada", "success");
      carregar();
    } catch (err) {
      Swal.fire("Erro", err?.message || "Falha ao salvar", "error");
    }
  };

  const onExcluir = async (c) => {
    const step1 = await Swal.fire({
      title: `Excluir ${c.nome}?`,
      text: "Essa ação é irreversível. Deseja continuar?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, continuar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33",
    });
    if (!step1.isConfirmed) return;

    const step2 = await Swal.fire({
      title: "Confirmação final",
      html: `<p>Todos os dados da coordenação <strong>${c.nome}</strong> e seus vínculos serão excluídos permanentemente.</p><p><strong>Essa ação não pode ser desfeita.</strong></p>`,
      icon: "error",
      showCancelButton: true,
      confirmButtonText: "Excluir definitivamente",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33",
    });
    if (!step2.isConfirmed) return;

    try {
      await adminCoordenacoesApi.excluir(instId, c.id);
      Swal.fire("OK", "Coordenação excluída", "success");
      carregar();
    } catch (err) {
      Swal.fire("Erro", err?.message || "Falha ao excluir", "error");
    }
  };

  const onVincularCoord = async (c) => {
    try {
      const usuarios = await buscarCoordenadoresDisponiveis();
      if (usuarios.length === 0) {
        Swal.fire(
          "Aviso",
          "Nenhum coordenador disponível nesta instituição",
          "info",
        );
        return;
      }
      const jaVinculados = new Set(
        (detalhes[c.id]?.coordenadores || []).map((x) => String(x.usuarioId)),
      );
      const disponiveis = usuarios.filter(
        (u) => !jaVinculados.has(String(u.id)),
      );
      if (disponiveis.length === 0) {
        Swal.fire("Aviso", "Todos os coordenadores já estão vinculados.", "info");
        return;
      }
      const opts = disponiveis
        .map((u) => `<option value="${u.id}">${u.nome} (${u.email})</option>`)
        .join("");
      const { value, isConfirmed } = await Swal.fire({
        title: "Vincular coordenador",
        html: `<select id="sw-u" class="swal2-input"><option value="">Selecione um coordenador...</option>${opts}</select>`,
        showCancelButton: true,
        confirmButtonText: "Vincular",
        cancelButtonText: "Cancelar",
        preConfirm: () => {
          const val = document.getElementById("sw-u").value;
          if (!val) {
            Swal.showValidationMessage("Selecione um coordenador");
            return false;
          }
          return val;
        },
      });
      if (!isConfirmed) return;
      await adminCoordenacoesApi.vincularCoordenador(instId, c.id, value);
      Swal.fire("OK", "Coordenador vinculado", "success");
      await carregarDetalhes(c.id);
    } catch (err) {
      Swal.fire("Erro", err?.message || "Falha ao vincular", "error");
    }
  };

  const onDesvincularCoordenador = async (coordId, userId) => {
    // Não permite remover o último coordenador — coordenação deve ter pelo menos um.
    const total = detalhes[coordId]?.coordenadores?.length || 0;
    if (total <= 1) {
      Swal.fire(
        "Atenção",
        "Toda coordenação precisa de ao menos um coordenador. Vincule outro antes de remover este.",
        "warning",
      );
      return;
    }
    const ok = await Swal.fire({
      title: "Remover coordenador?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, remover",
      cancelButtonText: "Cancelar",
    });
    if (!ok.isConfirmed) return;
    try {
      await adminCoordenacoesApi.desvincularCoordenador(
        instId,
        coordId,
        userId,
      );
      Swal.fire("OK", "Coordenador removido", "success");
      await carregarDetalhes(coordId);
    } catch (err) {
      Swal.fire("Erro", err?.message || "Falha ao remover", "error");
    }
  };

  return (
    <div className="admin-inst-coords">
      <header className="topo">
        <h2>Coordenações</h2>
        <button className="primary" onClick={onCriar}>
          Nova coordenação
        </button>
      </header>
      {loading ? (
        <p>Carregando...</p>
      ) : (
        <ul className="lista">
          {coords.length === 0 && (
            <li className="vazio">Nenhuma coordenação cadastrada.</li>
          )}
          {coords.map((c) => (
            <li key={c.id} className="coord-item">
              <div className="coord-header">
                <div>
                  <strong>{c.nome}</strong>
                </div>
                <div className="acoes">
                  <button
                    className="toggle-btn"
                    onClick={() => {
                      if (aberta === c.id) {
                        setAberta(null);
                      } else {
                        setAberta(c.id);
                        if (!detalhes[c.id]) {
                          carregarDetalhes(c.id);
                        }
                      }
                    }}
                  >
                    {aberta === c.id ? "Fechar" : "Gerenciar"}
                  </button>
                  <button onClick={() => onEditar(c)}>Editar</button>
                  <button className="danger" onClick={() => onExcluir(c)}>
                    Excluir
                  </button>
                </div>
              </div>
              {aberta === c.id && (
                <div className="coord-details">
                  <section className="sub-section">
                    <h3>Coordenadores vinculados</h3>
                    <button
                      className="add-btn"
                      onClick={() => onVincularCoord(c)}
                    >
                      + Vincular coordenador
                    </button>
                    {detalhes[c.id]?.coordenadores &&
                    detalhes[c.id].coordenadores.length > 0 ? (
                      <ul className="vinculos-list">
                        {detalhes[c.id].coordenadores.map((coord) => (
                          <li key={coord.usuarioId} className="vinculo-item">
                            <div className="vinculo-info">
                              <strong>{coord.nome}</strong>
                              <span className="email">{coord.email}</span>
                            </div>
                            <button
                              className="remover-btn"
                              onClick={() =>
                                onDesvincularCoordenador(
                                  c.id,
                                  coord.usuarioId,
                                )
                              }
                              title="Remover coordenador"
                            >
                              ×
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="vazio-subitem">
                        Nenhum coordenador vinculado.
                      </p>
                    )}
                  </section>

                  <section className="sub-section">
                    <h3>Turmas vinculadas</h3>
                    {detalhes[c.id]?.turmas &&
                    detalhes[c.id].turmas.length > 0 ? (
                      <ul className="vinculos-list">
                        {detalhes[c.id].turmas.map((t) => (
                          <li key={t.id} className="vinculo-item">
                            <div className="vinculo-info">
                              <strong>{t.nome}</strong>
                              {t.complemento && (
                                <span className="email">{t.complemento}</span>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="vazio-subitem">
                        Nenhuma turma vinculada a esta coordenação.
                      </p>
                    )}
                  </section>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
